/**
 * Detective Service
 *
 * Handles Story Detective operations: fetching stories, managing attempts,
 * and scoring quizzes.
 */

import { supabase, isSupabaseConfigured } from '@/lib/supabase'
import type {
  DetectiveStory,
  DetectiveAttempt,
  DetectiveAttemptInsert,
  ChallengeDifficulty,
  AgeRange,
} from '@/types/database'
import type { StoryElement } from '@/types/domain'

// =============================================================================
// Types
// =============================================================================

export interface DetectiveQuestion {
  element: StoryElement
  question: string
  options: string[]
  correct_index: number
}

export interface DetectiveAnswerKey {
  [element: string]: string
}

export interface StoryFilters {
  difficulty?: ChallengeDifficulty
  category?: string
  ageRange?: AgeRange
  featuredOnly?: boolean
}

export interface AttemptResult {
  attempt: DetectiveAttempt
  score: number
  totalQuestions: number
  correctAnswers: number[]
  explanations: DetectiveAnswerKey
}

// =============================================================================
// Story Queries
// =============================================================================

/**
 * Get all published detective stories, optionally filtered
 */
export async function getDetectiveStories(
  filters?: StoryFilters
): Promise<DetectiveStory[]> {
  if (!isSupabaseConfigured()) return []

  let query = supabase
    .from('detective_stories')
    .select('*')
    .not('published_at', 'is', null)
    .is('deleted_at', null)
    .order('is_featured', { ascending: false })
    .order('created_at', { ascending: false })

  if (filters?.difficulty) {
    query = query.eq('difficulty', filters.difficulty)
  }

  if (filters?.category && filters.category !== 'all') {
    query = query.eq('category', filters.category)
  }

  if (filters?.ageRange) {
    query = query.contains('age_ranges', [filters.ageRange])
  }

  if (filters?.featuredOnly) {
    query = query.eq('is_featured', true)
  }

  const { data, error } = await query

  if (error) {
    console.error('Error fetching detective stories:', error)
    throw error
  }

  return (data as DetectiveStory[]) || []
}

/**
 * Get a single detective story by ID
 */
export async function getDetectiveStory(
  storyId: string
): Promise<DetectiveStory | null> {
  if (!isSupabaseConfigured()) return null

  const { data, error } = await supabase
    .from('detective_stories')
    .select('*')
    .eq('id', storyId)
    .not('published_at', 'is', null)
    .is('deleted_at', null)
    .single()

  if (error) {
    if (error.code === 'PGRST116') return null // Not found
    console.error('Error fetching detective story:', error)
    throw error
  }

  return data as DetectiveStory
}

/**
 * Get featured stories for dashboard display
 */
export async function getFeaturedDetectiveStories(
  limit: number = 3
): Promise<DetectiveStory[]> {
  if (!isSupabaseConfigured()) return []

  const { data, error } = await supabase
    .from('detective_stories')
    .select('*')
    .eq('is_featured', true)
    .not('published_at', 'is', null)
    .is('deleted_at', null)
    .order('created_at', { ascending: false })
    .limit(limit)

  if (error) {
    console.error('Error fetching featured detective stories:', error)
    throw error
  }

  return (data as DetectiveStory[]) || []
}

/**
 * Get story categories
 */
export async function getDetectiveCategories(): Promise<string[]> {
  if (!isSupabaseConfigured()) return []

  const { data, error } = await supabase
    .from('detective_stories')
    .select('category')
    .not('published_at', 'is', null)
    .is('deleted_at', null)

  if (error) {
    console.error('Error fetching detective categories:', error)
    return []
  }

  const categories = new Set(
    (data as { category: string }[]).map(d => d.category)
  )
  return Array.from(categories).sort()
}

// =============================================================================
// Attempt Queries
// =============================================================================

/**
 * Get all attempts for a child
 */
export async function getAttemptsForChild(
  childId: string
): Promise<DetectiveAttempt[]> {
  if (!isSupabaseConfigured()) return []

  const { data, error } = await supabase
    .from('detective_attempts')
    .select('*')
    .eq('child_id', childId)
    .order('created_at', { ascending: false })

  if (error) {
    console.error('Error fetching detective attempts:', error)
    throw error
  }

  return (data as DetectiveAttempt[]) || []
}

/**
 * Get attempts for a specific story by a child
 */
export async function getAttemptsForStory(
  childId: string,
  storyId: string
): Promise<DetectiveAttempt[]> {
  if (!isSupabaseConfigured()) return []

  const { data, error } = await supabase
    .from('detective_attempts')
    .select('*')
    .eq('child_id', childId)
    .eq('story_id', storyId)
    .order('created_at', { ascending: false })

  if (error) {
    console.error('Error fetching story attempts:', error)
    throw error
  }

  return (data as DetectiveAttempt[]) || []
}

/**
 * Get a single attempt by ID
 */
export async function getAttempt(
  attemptId: string
): Promise<DetectiveAttempt | null> {
  if (!isSupabaseConfigured()) return null

  const { data, error } = await supabase
    .from('detective_attempts')
    .select('*')
    .eq('id', attemptId)
    .single()

  if (error) {
    if (error.code === 'PGRST116') return null
    console.error('Error fetching attempt:', error)
    throw error
  }

  return data as DetectiveAttempt
}

/**
 * Check if a child has already attempted a story today
 */
export async function hasAttemptedToday(
  childId: string,
  storyId: string
): Promise<boolean> {
  if (!isSupabaseConfigured()) return false

  const today = new Date().toISOString().split('T')[0]

  const { data, error } = await supabase
    .from('detective_attempts')
    .select('id')
    .eq('child_id', childId)
    .eq('story_id', storyId)
    .eq('attempt_date', today)
    .limit(1)

  if (error) {
    console.error('Error checking today attempt:', error)
    return false
  }

  return (data?.length ?? 0) > 0
}

/**
 * Get attempt with story data
 */
export async function getAttemptWithStory(
  attemptId: string
): Promise<{ attempt: DetectiveAttempt; story: DetectiveStory } | null> {
  if (!isSupabaseConfigured()) return null

  const { data, error } = await supabase
    .from('detective_attempts')
    .select(`
      *,
      detective_stories (*)
    `)
    .eq('id', attemptId)
    .single()

  if (error) {
    if (error.code === 'PGRST116') return null
    console.error('Error fetching attempt with story:', error)
    throw error
  }

  const attempt = data as DetectiveAttempt & { detective_stories: DetectiveStory }

  return {
    attempt: {
      ...attempt,
      detective_stories: undefined,
    } as DetectiveAttempt,
    story: attempt.detective_stories,
  }
}

// =============================================================================
// Attempt Mutations
// =============================================================================

/**
 * Start a new attempt for a story
 */
export async function startAttempt(
  childId: string,
  storyId: string
): Promise<DetectiveAttempt> {
  if (!isSupabaseConfigured()) {
    throw new Error('Supabase not configured')
  }

  const insert: DetectiveAttemptInsert = {
    child_id: childId,
    story_id: storyId,
    answers: {},
  }

  const { data, error } = await supabase
    .from('detective_attempts')
    .insert(insert as Record<string, unknown>)
    .select()
    .single()

  if (error) {
    // Check for unique constraint violation (already attempted today)
    if (error.code === '23505') {
      throw new Error('Already attempted this story today')
    }
    console.error('Error starting attempt:', error)
    throw error
  }

  return data as DetectiveAttempt
}

/**
 * Submit answers and calculate score
 */
export async function submitAttempt(
  attemptId: string,
  answers: Record<number, number>,
  timeTakenSeconds: number,
  story: DetectiveStory
): Promise<AttemptResult> {
  if (!isSupabaseConfigured()) {
    throw new Error('Supabase not configured')
  }

  const questions = story.questions as unknown as DetectiveQuestion[]
  const answerKey = story.answer_key as unknown as DetectiveAnswerKey

  // Calculate score
  let correctCount = 0
  const correctAnswers: number[] = []

  questions.forEach((question, index) => {
    const userAnswer = answers[index]
    if (userAnswer === question.correct_index) {
      correctCount++
      correctAnswers.push(index)
    }
  })

  // Update attempt with results
  const { data, error } = await supabase
    .from('detective_attempts')
    .update({
      answers: answers,
      score: correctCount,
      time_taken_seconds: timeTakenSeconds,
      completed_at: new Date().toISOString(),
    } as Record<string, unknown>)
    .eq('id', attemptId)
    .select()
    .single()

  if (error) {
    console.error('Error submitting attempt:', error)
    throw error
  }

  return {
    attempt: data as DetectiveAttempt,
    score: correctCount,
    totalQuestions: questions.length,
    correctAnswers,
    explanations: answerKey,
  }
}

// =============================================================================
// Statistics
// =============================================================================

/**
 * Get detective stats for a child
 */
export async function getDetectiveStats(childId: string): Promise<{
  storiesCompleted: number
  totalScore: number
  perfectScores: number
  averageScore: number
}> {
  if (!isSupabaseConfigured()) {
    return {
      storiesCompleted: 0,
      totalScore: 0,
      perfectScores: 0,
      averageScore: 0,
    }
  }

  const { data, error } = await supabase
    .from('detective_attempts')
    .select('score')
    .eq('child_id', childId)
    .not('completed_at', 'is', null)

  if (error) {
    console.error('Error fetching detective stats:', error)
    return {
      storiesCompleted: 0,
      totalScore: 0,
      perfectScores: 0,
      averageScore: 0,
    }
  }

  const attempts = data as { score: number }[]
  const storiesCompleted = attempts.length
  const totalScore = attempts.reduce((sum, a) => sum + (a.score || 0), 0)
  const perfectScores = attempts.filter(a => a.score === 5).length // Assuming 5 questions per story
  const averageScore = storiesCompleted > 0 ? totalScore / storiesCompleted : 0

  return {
    storiesCompleted,
    totalScore,
    perfectScores,
    averageScore: Math.round(averageScore * 10) / 10,
  }
}

/**
 * Get stories with attempt status for a child
 */
export async function getStoriesWithAttemptStatus(
  childId: string,
  filters?: StoryFilters
): Promise<Array<DetectiveStory & { hasAttempted: boolean; bestScore: number | null }>> {
  const stories = await getDetectiveStories(filters)
  const attempts = await getAttemptsForChild(childId)

  // Create a map of story_id -> best score
  const attemptMap = new Map<string, number>()
  attempts.forEach(attempt => {
    if (attempt.score !== null) {
      const current = attemptMap.get(attempt.story_id)
      if (current === undefined || attempt.score > current) {
        attemptMap.set(attempt.story_id, attempt.score)
      }
    }
  })

  return stories.map(story => ({
    ...story,
    hasAttempted: attemptMap.has(story.id),
    bestScore: attemptMap.get(story.id) ?? null,
  }))
}

// =============================================================================
// Helper Functions
// =============================================================================

/**
 * Parse questions from JSONB
 */
export function parseQuestions(story: DetectiveStory): DetectiveQuestion[] {
  return story.questions as unknown as DetectiveQuestion[]
}

/**
 * Parse answer key from JSONB
 */
export function parseAnswerKey(story: DetectiveStory): DetectiveAnswerKey {
  return story.answer_key as unknown as DetectiveAnswerKey
}

/**
 * Get score label based on performance
 */
export function getScoreLabel(score: number, total: number): {
  label: string
  emoji: string
  color: string
} {
  const percentage = (score / total) * 100

  if (percentage === 100) {
    return { label: 'Perfect!', emoji: '🌟', color: 'var(--color-kids-accent)' }
  } else if (percentage >= 80) {
    return { label: 'Great job!', emoji: '🎉', color: 'var(--color-kids-success)' }
  } else if (percentage >= 60) {
    return { label: 'Good work!', emoji: '👍', color: 'var(--color-kids-secondary)' }
  } else if (percentage >= 40) {
    return { label: 'Keep practicing!', emoji: '💪', color: 'var(--color-kids-primary)' }
  } else {
    return { label: 'Try again!', emoji: '📚', color: 'var(--color-kids-text-muted)' }
  }
}

/**
 * Get element display name
 */
export function getElementDisplayName(element: StoryElement): string {
  const names: Record<StoryElement, string> = {
    problem: 'Problem',
    goal: 'Goal',
    obstacle: 'Obstacle',
    turning_point: 'Turning Point',
    resolution: 'Resolution',
    change: 'Change',
  }
  return names[element] || element
}
